import { useCallback, useEffect, useState } from 'react';
import {
  useBlocker,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router-dom';
import { toast } from 'react-toastify';
import { skipToken } from '@reduxjs/toolkit/query';
import ModalMockFillButton from 'components/ModalMockFillButton/ModalMockFillButton';
import AppRoutes from 'constants/AppRoutes';
import { DATA_SETS_ENUM_TYPES } from 'constants/dataSets';
import {
  NACE_CODE, NACE_SECTION, PROFIT_CENTRE_NAME,
  VALUE_CHAIN_LIST,
  VALUE_CHAIN_SECTION,
} from 'constants/formFields';
import { BLOCKER_STATES } from 'constants/general';
import useDataSetsDate from 'hooks/useDataSetsDate';
import useFieldsErrors from 'hooks/useFieldsErrors';
import useGeoLocations from 'hooks/useGeoLocations';
import useScrollToError from 'hooks/useScrollToError';
import useValueChainPosition from 'hooks/useValueChainPosition';
import i18n from 'i18n';
import { getCreateProfitCentrePageMockValues } from 'mocks/dataSetsCreatePageMocks';
import {
  useEnumQuery,
  useGetNaceCodeTreeQuery,
  useGetOrgStructureQuery,
  useLazyProfitCenterRecordQuery,
  useProfitCenterCreateMutation, useProfitCenterRecordQuery,
  useProfitCenterRecordUpdateMutation,
} from 'store/slices/dataSets/apis/dataSetsApi';
import { selectEnums, selectNaceCodes } from 'store/slices/dataSets/selectors';
import { useUserMeQuery } from 'store/slices/user/apis/user';
import getRequestDataForProfitCentre from 'utils/formHelpers/getRequestDataForProfitCentre';

import useCreateProfitCentreForm from './hooks/useCreateProfitCentreForm';
import CreateProfitCentreView from './CreateProfitCentreView';

import type IDataSetsOutletContext from 'layout/DataSetsLayout/interfaces/IDataSetsOutletContext';
import type { ReactElement } from 'react';
import type { INaceExtractedValue } from 'store/slices/dataSets/interfaces/INaceExtractedValue';
import type IProfitCenterRecordRequest from 'store/slices/dataSets/interfaces/IProfitCenterRecordRequest';
import type { TCreateProfitCentreSchema } from 'utils/validators';

function CreateProfitCentre(): ReactElement {
  const navigate = useNavigate();
  const { recordId = '' } = useParams();
  const { setTitleAction } = useOutletContext<IDataSetsOutletContext>();
  const { data: userData, isLoading: isUserLoading } = useUserMeQuery();
  const companyCurrency = userData?.data?.company?.currency;
  const [profitCenterCreate, { isLoading: isProfitCenterCreateLoading }] = useProfitCenterCreateMutation();
  const { data: orgStructureData, isLoading: isOrgStructureLoading } = useGetOrgStructureQuery();

  const [profitCenterRecordUpdate, { isLoading: isProfitCenterRecordLoading }] = useProfitCenterRecordUpdateMutation();
  const [fetchProfitCenterRecord] = useLazyProfitCenterRecordQuery();
  const { setFieldsErrors } = useFieldsErrors();

  const fetchQuery = recordId ? {
    recordId,
  } : skipToken;
  const { isLoading } = useProfitCenterRecordQuery(fetchQuery);

  const { data: enumData, isLoading: isEnumLoading } = useEnumQuery({
    types: [DATA_SETS_ENUM_TYPES.currency, DATA_SETS_ENUM_TYPES.ownership, DATA_SETS_ENUM_TYPES.valueChainSection,
      DATA_SETS_ENUM_TYPES.businessUnitType, DATA_SETS_ENUM_TYPES.assetCategory, DATA_SETS_ENUM_TYPES.ownedLeased],
  }, {
    selectFromResult: (result) => ({ ...result, data: selectEnums(result.data) }),
  });

  const { data: naceCodesData, isLoading: isNaceCodesTreeLoading } = useGetNaceCodeTreeQuery(undefined, {
    selectFromResult: (result) => ({ ...result, data: selectNaceCodes(result.data) }),
  });

  const isPageLoading = isLoading || isEnumLoading || isProfitCenterCreateLoading
  || isProfitCenterRecordLoading || isUserLoading || isNaceCodesTreeLoading || isOrgStructureLoading;
  const profitCenterEndpoint = recordId ? profitCenterRecordUpdate : profitCenterCreate;
  const {
    control,
    handleSubmit,
    isDirty,
    reset,
    setError,
    fieldsName,
    watch,
    setValue,
    formState,
  } = useCreateProfitCentreForm({ recordId, isPageLoading });

  const {
    handleGetVcOptions,
    vcOptionsList,
    listPosition,
  } = useValueChainPosition(watch(VALUE_CHAIN_LIST)?.value);

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => isDirty
      && currentLocation.pathname !== nextLocation.pathname
      && !isConfirmationModalOpen);

  useEffect(() => {
    if (blocker.state === BLOCKER_STATES.blocked) {
      if (isDirty) {
        setIsConfirmationModalOpen(true);
      } else {
        blocker.proceed?.();
      }
    }
  }, [blocker, isDirty]);

  useScrollToError({ formState });

  const onProfitCentreSubmit = handleSubmit((values) => {
    const requestData: IProfitCenterRecordRequest = getRequestDataForProfitCentre({ values, recordId });

    profitCenterEndpoint(requestData).unwrap()
      .then(
        () => {
          toast.success(i18n.t(
            recordId ? 'notifications.recordHasBeenUpdated'
              : 'notifications.recordHasBeenAdded',
          ));
          reset(undefined, { keepValues: true });

          if (recordId) {
            setTimeout(() => {
              fetchProfitCenterRecord({ recordId });
            }, 10000);
          }

          navigate(AppRoutes.profitCentres);
        },
      ).catch((errors) => {
        setFieldsErrors<TCreateProfitCentreSchema>({ errors, setError, fieldsName });
      });
  });

  const onHandleCloseConfirmation = (isOpen = false) => {
    if (!isConfirmationModalOpen && !isDirty) {
      navigate(-1);
    } else {
      setIsConfirmationModalOpen(isOpen);
    }
  };

  const onDiscardChangesConfirm = () => {
    const currentLocation = blocker?.location?.pathname;
    if (currentLocation) {
      navigate(currentLocation);
    } else {
      navigate(-1);
    }
    reset(undefined, { keepValues: true });
  };

  const naceDivisions = (watch(NACE_SECTION) as INaceExtractedValue)?.divisions || [];

  const onNaceSelectionChange = () => {
    setValue(NACE_CODE, null);
  };

  const {
    dateOpened, onDateOpenedChange,
    startDate, onStartDateChange,
  } = useDataSetsDate<TCreateProfitCentreSchema>({ watch, setValue });

  const {
    onFindCountries, onFindStates, onFindCities, isStatesDisabled, onChangeCountry, onChangeState,
    isCitiesDisabled, defaultCountries,
  } = useGeoLocations<TCreateProfitCentreSchema>({ watch, setValue });

  const isMockFillVisible = !recordId && process.env.REACT_APP_API?.includes('api.dev');

  const onFillMockData = useCallback(() => {
    const valueChainListOption = watch(VALUE_CHAIN_LIST);
    const valueChainSectionOption = watch(VALUE_CHAIN_SECTION);

    reset({
      ...watch(),
      ...getCreateProfitCentrePageMockValues({
        enumData,
        naceCodesData,
        countryOptions: defaultCountries,
        companyCurrency,
        orgStructureData: orgStructureData?.data,
      }),
      [VALUE_CHAIN_LIST]: valueChainListOption?.value ? valueChainListOption : vcOptionsList?.[0],
      [VALUE_CHAIN_SECTION]: valueChainSectionOption?.value ? valueChainSectionOption : listPosition?.[0],
    });
  }, [
    companyCurrency,
    defaultCountries,
    enumData,
    listPosition,
    naceCodesData,
    orgStructureData,
    reset,
    vcOptionsList,
    watch,
  ]);

  useEffect(() => {
    if (!isMockFillVisible) {
      setTitleAction(null);
      return undefined;
    }

    setTitleAction(
      <ModalMockFillButton
        onClick={onFillMockData}
        disabled={isPageLoading}
      />,
    );

    return () => {
      setTitleAction(null);
    };
  }, [isMockFillVisible, isPageLoading, onFillMockData, setTitleAction]);

  return (
    <CreateProfitCentreView
      control={control}
      onProfitCentreSubmit={onProfitCentreSubmit}
      onHandleCloseConfirmation={onHandleCloseConfirmation}
      isConfirmationModalOpen={isConfirmationModalOpen}
      onDiscardChangesConfirm={onDiscardChangesConfirm}
      isPageLoading={isPageLoading}
      onDateOpenedChange={onDateOpenedChange}
      dateOpened={dateOpened}
      onStartDateChange={onStartDateChange}
      startDate={startDate}
      onFindCountries={onFindCountries}
      onFindStates={onFindStates}
      onFindCities={onFindCities}
      isStatesDisabled={isStatesDisabled}
      onChangeCountry={onChangeCountry}
      onChangeState={onChangeState}
      isCitiesDisabled={isCitiesDisabled}
      enumData={enumData}
      naceCodesData={naceCodesData}
      onNaceSelectionChange={onNaceSelectionChange}
      naceDivisions={naceDivisions}
      orgStructureData={orgStructureData?.data}
      logicalName={watch(PROFIT_CENTRE_NAME)}
      setValue={setValue}
      defaultCountries={defaultCountries}
      vcOptionsList={vcOptionsList}
      handleGetVcOptions={handleGetVcOptions}
      listPosition={listPosition}
    />
  );
}

export default CreateProfitCentre;
